import { useState, useEffect, useCallback } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Box,
} from '@mui/material';
import theme from 'theme';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { formatDate, snapToArray, setDateHourToMidnight } from '../../utils';
import { Portlet, PortletContent, PortletHeader, PortletLabel } from '../../components/Portlets';
import { Cell } from '../../components/Cell';
import { useAuth } from '../../auth';
import { useFirestore } from '../../utils/firebase';
import { apiClient } from '../../api-client';

const OutOfOfficesTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [outOfOffices, setOutOfOffices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const database = useFirestore();
  const { user, getToken } = useAuth();

  const loadOutOfOffices = useCallback(async () => {
    const queryRequests = query(
      collection(database, 'out_of_offices'),
      where('userId', '==', user.uid),
      orderBy('requestedAt', 'desc'),
    );

    try {
      const snapshot = await getDocs(queryRequests);
      const result = snapToArray(snapshot);
      setOutOfOffices(result);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [database, user.uid]);

  useEffect(() => {
    setLoading(true);
    loadOutOfOffices();
  }, [loadOutOfOffices, user]);

  const handleDeleteEvent = async (eventFrom, eventTo, eventType) => {
    const url = '/calendar/event';
    const title = eventType === 'Sick Leave' ? 'Out of office' : eventType;
    const body = {
      calendarType: 'out-of-office',
      title,
      eventFrom: setDateHourToMidnight(eventFrom.toDate()),
      eventTo: setDateHourToMidnight(eventTo.toDate()),
      userId: user.uid,
      tenantId: user.tenant.id,
    };
    const response = await apiClient('DELETE', url, getToken, body);
    if (response.statusCode > 204) {
      throw response;
    }
  };

  const deleteOutOfOffice = async (id, startDate, endDate, requestType) => {
    try {
      await deleteDoc(doc(database, 'out_of_offices', id));
      enqueueSnackbar('Out of office request successfully deleted', {
        variant: 'success',
      });
      loadOutOfOffices();
      try {
        await handleDeleteEvent(startDate, endDate, requestType);
      } catch (err) {
        enqueueSnackbar('Error when deleting event', {
          variant: 'error',
        });
        console.error(err);
      }
    } catch (error) {
      enqueueSnackbar('Error when deleting an out of office request', {
        variant: 'error',
      });
      console.error(error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hideActionsColumnAndCell = requests =>
    requests.filter(request => request.startDate.toDate() > new Date()).length > 0;

  return (
    <Portlet
      sx={{ background: 'none', border: 'none', boxShadow: 'none' }}
      outlined={1}
      squared={false}
    >
      {outOfOffices.length ? (
        <PortletHeader noDivider>
          <PortletLabel
            subtitle={`${outOfOffices.length} in total`}
            title="Latest Requests"
            sx={{
              '@media (max-width:480px)': {
                '& h5': {
                  fontSize: '1.3rem',
                },
                '& h6': {
                  marginLeft: '0.5rem',
                  fontSize: '0.7rem',
                  width: '3rem',
                },
              },
            }}
          />
        </PortletHeader>
      ) : (
        ''
      )}
      <PerfectScrollbar>
        <PortletContent
          isLoading={isLoading}
          sx={{ minWidth: '600px', overflow: 'auto' }}
          noPadding
        >
          {outOfOffices.length ? (
            <>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Request Type</TableCell>
                    <TableCell>Requested at</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Description</TableCell>
                    {hideActionsColumnAndCell(outOfOffices) && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {outOfOffices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(({ id, description, startDate, endDate, requestType, requestedAt }) => (
                      <TableRow sx={{ cursor: 'pointer' }} hover key={id} data-cy={description}>
                        <Cell>{requestType}</Cell>
                        <Cell>{formatDate(requestedAt)}</Cell>
                        <Cell>{formatDate(startDate)}</Cell>
                        <Cell>{formatDate(endDate)}</Cell>
                        <Cell>{description}</Cell>
                        {hideActionsColumnAndCell(outOfOffices) && (
                          <Cell>
                            {startDate.toDate() > new Date() ? (
                              <Button
                                size="small"
                                onClick={() => {
                                  deleteOutOfOffice(id, startDate, endDate, requestType);
                                }}
                                sx={{
                                  backgroundColor: theme.palette.danger.main,
                                  color: theme.palette.common.white,
                                  '&:hover': {
                                    backgroundColor: '#c62828',
                                  },
                                }}
                                data-cy="deleteButton"
                              >
                                DELETE
                              </Button>
                            ) : (
                              <div />
                            )}
                          </Cell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                component="div"
                count={outOfOffices.length}
                page={page}
              />
            </>
          ) : (
            <Box sx={{ padding: 24, paddingTop: 0, fontWeight: 300, color: '#777' }}>
              No out of office requests found
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
    </Portlet>
  );
};

export default OutOfOfficesTable;
